import { IconButton } from "@faire/slate/components/icon-button";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

export const EASE_OUT_EXPO = [0.22, 1, 0.36, 1] as const;

/** Increments a key to force a full remount so entrance animations replay. */
export const useReplayKey = (): [number, () => void] => {
  const [replayKey, setReplayKey] = useState(0);
  return [replayKey, () => setReplayKey((current) => current + 1)];
};

/** Counts up from 0 to `target` once `active` becomes true. Snaps to target under reduced motion. */
export const useCountUp = (
  target: number,
  durationMs: number,
  active: boolean
): number => {
  const reduceMotion = useReducedMotion() ?? false;
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    if (reduceMotion) {
      setValue(target);
      return;
    }
    let animationFrameId: number;
    const startTimeMs = performance.now();
    const tick = (): void => {
      const elapsedMs = performance.now() - startTimeMs;
      const progress = Math.min(elapsedMs / durationMs, 1);
      setValue(Math.round(target * (1 - (1 - progress) ** 3)));
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(tick);
      }
    };
    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [active, target, durationMs, reduceMotion]);

  return value;
};

const FAIRE_LOGO_SRC = "https://cdn.faire.com/static/logo.svg";

export interface SignupStepHeaderProps {
  /** 0-100. */
  progressPercent: number;
  onBack?: () => void;
}

/**
 * Recreation of the signup flow's CompactNav (logo + thin progress bar),
 * matching the real chrome around LEGAL_BUSINESS_NAME and SELECT_NET_TERMS
 * so the transition reads as part of the same flow.
 */
export const SignupStepHeader: React.FC<SignupStepHeaderProps> = ({
  progressPercent,
  onBack,
}) => {
  const clampedProgress = Math.min(Math.max(0, progressPercent), 100);

  return (
    <>
      <header className="bg-fs-surface-primary grid w-full grid-cols-3 items-center">
        <div className="justify-self-start">
          {onBack ? (
            <IconButton
              aria-label="Back"
              onClick={onBack}
              shape="square"
              variant="tertiary"
              Icon={ChevronLeft}
            />
          ) : null}
        </div>
        <div className="flex h-6 flex-col items-center justify-center py-(--slate-spacing-element-large) md:h-8">
          <img
            src={FAIRE_LOGO_SRC}
            alt="Faire Logo"
            className="w-[88px] md:w-[118px]"
          />
        </div>
        <span />
      </header>
      <div
        className="bg-fs-surface-secondary relative h-[4px] w-full"
        role="progressbar"
        aria-label="Signup progress"
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="bg-fs-action-surface-default h-full transition-[width] duration-1000 ease-in-out"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </>
  );
};

export interface VariantExplainerProps {
  eyebrow: string;
  title: string;
  hypothesis: string;
}

/** Small always-visible container pairing a variant's name with the hypothesis behind it. */
export const VariantExplainer: React.FC<VariantExplainerProps> = ({
  eyebrow,
  title,
  hypothesis,
}) => (
  <div className="border-fs-stroke-divider bg-fs-surface-primary flex flex-col gap-(--slate-dimensions-2) rounded-(--slate-dimensions-3) border p-(--slate-dimensions-4) shadow-sm">
    <span className="type-fs-label text-fs-text-subdued uppercase">
      {eyebrow}
    </span>
    <p className="type-fs-paragraph-medium text-fs-text-primary">{title}</p>
    <p className="type-fs-paragraph text-fs-text-subdued">{hypothesis}</p>
  </div>
);

export type ConfettiPaletteKey = "brand" | "money" | "pastel";

/** Named confetti color presets, controllable from the LiveToolbar. */
export const CONFETTI_PALETTES: Record<ConfettiPaletteKey, readonly string[]> =
  {
    brand: ["#F2B705", "#F25C54", "#3D7068", "#E86A92", "#5B8C5A", "#1B1B1B"],
    money: ["#1B7A43", "#2FA35C", "#E4C05C", "#F4D976", "#0F5C33", "#0B4023"],
    pastel: ["#FFD6E8", "#D6F5E3", "#D6E4FF", "#FFF3C4", "#E8D6FF", "#FFE0D6"],
  };

export type ConfettiPattern = "rain" | "cornerBurst";

interface RainPiece {
  id: number;
  leftPercent: number;
  delaySeconds: number;
  durationSeconds: number;
  color: string;
  sizePx: number;
  rotateDeg: number;
  driftPx: number;
}

const buildRainConfetti = (
  count: number,
  colors: readonly string[]
): RainPiece[] =>
  Array.from({ length: count }, (_, index) => ({
    id: index,
    leftPercent: Math.random() * 100,
    delaySeconds: Math.random() * 0.5,
    durationSeconds: 2.4 + Math.random() * 1.6,
    color: colors[index % colors.length],
    sizePx: 6 + Math.random() * 7,
    rotateDeg: Math.random() * 720 - 360,
    driftPx: (Math.random() - 0.5) * 140,
  }));

const CORNERS = ["topLeft", "topRight", "bottomLeft", "bottomRight"] as const;
type Corner = (typeof CORNERS)[number];

/** Origin styles for each corner, so the burst reads as diagonal outward motion. */
const CORNER_ORIGIN: Record<Corner, Record<string, number>> = {
  topLeft: { top: 0, left: 0 },
  topRight: { top: 0, right: 0 },
  bottomLeft: { bottom: 0, left: 0 },
  bottomRight: { bottom: 0, right: 0 },
};

const CORNER_SIGN: Record<Corner, { signX: number; signY: number }> = {
  topLeft: { signX: 1, signY: 1 },
  topRight: { signX: -1, signY: 1 },
  bottomLeft: { signX: 1, signY: -1 },
  bottomRight: { signX: -1, signY: -1 },
};

interface CornerBurstPiece {
  id: number;
  corner: Corner;
  delaySeconds: number;
  durationSeconds: number;
  color: string;
  sizePx: number;
  rotateDeg: number;
  travelXVw: number;
  travelYVh: number;
}

const buildCornerBurstConfetti = (
  count: number,
  colors: readonly string[]
): CornerBurstPiece[] =>
  Array.from({ length: count }, (_, index) => {
    const corner = CORNERS[index % CORNERS.length];
    const { signX, signY } = CORNER_SIGN[corner];
    // Spray around the pure 45deg diagonal so it reads as a burst, not a single line.
    const angleJitterDeg = (Math.random() - 0.5) * 50;
    const angleRad = (45 + angleJitterDeg) * (Math.PI / 180);
    const distance = 35 + Math.random() * 45;

    return {
      id: index,
      corner,
      delaySeconds: Math.random() * 0.35,
      durationSeconds: 0.9 + Math.random() * 0.7,
      color: colors[index % colors.length],
      sizePx: 6 + Math.random() * 7,
      rotateDeg: Math.random() * 720 - 360,
      travelXVw: signX * distance * Math.cos(angleRad),
      travelYVh: signY * distance * Math.sin(angleRad),
    };
  });

interface ConfettiProps {
  pieceCount?: number;
  colors?: readonly string[];
  pattern?: ConfettiPattern;
}

/** Decorative confetti used by the net terms reveal. Renders nothing under reduced-motion preference. */
export const Confetti: React.FC<ConfettiProps> = ({
  pieceCount = 56,
  colors = CONFETTI_PALETTES.brand,
  pattern = "rain",
}) => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return null;
  }

  return pattern === "cornerBurst" ? (
    <CornerBurstConfetti pieceCount={pieceCount} colors={colors} />
  ) : (
    <RainConfetti pieceCount={pieceCount} colors={colors} />
  );
};

const RainConfetti: React.FC<{
  pieceCount: number;
  colors: readonly string[];
}> = ({ pieceCount, colors }) => {
  const pieces = useMemo(
    () => buildRainConfetti(pieceCount, colors),
    [pieceCount, colors]
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {pieces.map((piece) => (
        <motion.span
          key={piece.id}
          className="absolute top-0 block rounded-[1px]"
          style={{
            left: `${piece.leftPercent}%`,
            width: piece.sizePx,
            height: piece.sizePx * 1.5,
            backgroundColor: piece.color,
          }}
          initial={{ y: "-12vh", opacity: 0, rotate: 0 }}
          animate={{
            y: "116vh",
            x: piece.driftPx,
            rotate: piece.rotateDeg,
            opacity: [0, 1, 1, 0.85],
          }}
          transition={{
            duration: piece.durationSeconds,
            delay: piece.delaySeconds,
            ease: "easeIn",
          }}
        />
      ))}
    </div>
  );
};

const CornerBurstConfetti: React.FC<{
  pieceCount: number;
  colors: readonly string[];
}> = ({ pieceCount, colors }) => {
  const pieces = useMemo(
    () => buildCornerBurstConfetti(pieceCount, colors),
    [pieceCount, colors]
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {pieces.map((piece) => (
        <motion.span
          key={piece.id}
          className="absolute block rounded-[1px]"
          style={{
            ...CORNER_ORIGIN[piece.corner],
            width: piece.sizePx,
            height: piece.sizePx * 1.5,
            backgroundColor: piece.color,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.5, rotate: 0 }}
          animate={{
            x: `${piece.travelXVw}vw`,
            y: `${piece.travelYVh}vh`,
            opacity: [0, 1, 1, 0],
            scale: 1,
            rotate: piece.rotateDeg,
          }}
          transition={{
            duration: piece.durationSeconds,
            delay: piece.delaySeconds,
            ease: EASE_OUT_EXPO,
          }}
        />
      ))}
    </div>
  );
};
